"use client"
import styled from "@emotion/styled"
import { Box, Typography } from "@mui/material"
import Image from "next/image"
import React from "react"
import { Pagination } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"
import { transformProperties } from "utils/simplifyData"
import { colors, mediaQueries, sizes } from "../theme/theme"
import CircleIconLink from "./bit/CircleIconLink"
import GeneralTextContent from "./bit/GeneralTextContent"

interface DetailsImageSwiperCardProps {
  item: DetailsImageSwiperCardItem
  buttonVariant?: "elevated" | "outlined" | undefined
  cardHeight?: string
}
export interface DetailsImageSwiperCardItem {
  title: any
  properties: {
    title: {
      value: string
    }
    text: {
      value: string
    }
    bullets: {
      richText: string
    }
    button: {
      links: {
        url: string
        name: string
      }[]
    }
  }
}

const StyledDetailsImageSwiperCard = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  border-radius: ${sizes.radius.large};
  background-color: ${colors.white};
  width: 100%;
  max-width: 1126px;
  overflow: hidden;
  box-shadow: ${colors.dropShadow.main};
  ${mediaQueries.desktopM} {
    flex-direction: row;
    &:nth-of-type(odd) {
      .card-details {
        order: 1;
      }
    }
  }
  ${mediaQueries.desktopXl} {
    max-width: 1148px;
  }
`
const CardDetails = styled(Box)`
  display: flex;
  flex-direction: column;
  width: 50%;
  border-bottom: 1px solid ${colors.lightGray};
  padding-bottom: ${sizes.baseTriple};
  padding: 40px ${sizes.baseQuadric} ${sizes.baseQuadric} ${sizes.baseQuadric};
  gap: ${sizes.baseTriple};
  flex: 0 0 auto;
  ${mediaQueries.mobile} {
    width: 100%;
    gap: 26px;
  }
  .card-details {
  }
  ${mediaQueries.mobile} {
    padding: ${sizes.baseQuadric} ${sizes.baseDouble};
    gap: 26px;
  }
`
const Line = styled(Box)`
  width: 100%;
  height: 1px;
  background-color: ${colors.lightGray};
`
const StyledGeneralTextContent = styled(GeneralTextContent)`
  gap: inherit;
  margin-bottom: auto;
  ${mediaQueries.mobile} {
    padding-top: 0;
    align-items: flex-start;
    .title {
      margin: 0;
    }
  }
`

const StyledLinksContainer = styled(Box)`
  display: flex;
  flex-flow: row wrap;
  gap: ${sizes.baseDouble};
  > * {
    border-right: 1px solid ${colors.lightGray};
    padding-inline-end: ${sizes.baseDouble};
    ${mediaQueries.mobile} {
      font-size: 14px;
      padding-inline-end: ${sizes.baseSingle};
    }
    &:last-child {
      border: none;
      padding-inline-end: 0;
    }
  }
  h5 {
    width: 100%;
    color: ${colors.red};
    text-align: start;
    border: none;
    ${mediaQueries.mobile} {
      font-size: 14px;
    }
  }
  ${mediaQueries.mobile} {
    gap: 14px;
  }
`
const StyledSwiper = styled(Swiper)`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 356px;
  width: 50%;
  order: 1;
  flex-grow: 1;
  overflow: hidden;
  img {
    object-fit: cover;
  }
  &.swiper {
    width: 100%;
    overflow: hidden;
    ${mediaQueries.mobile} {
      height: 267px;
    }
    .swiper-wrapper {
    }
  }
  ${mediaQueries.mobile} {
    height: 267px;
    order: 0;
  }
`

const StyledSwiperSlide = styled(SwiperSlide)`
  display: flex;
  width: 100%;
  height: 356px;
  img {
    width: 100%;
    height: 356px;
    object-fit: cover;
  }
  ${mediaQueries.mobile} {
    width: 100%;
    height: 267px;
    img {
      width: 100%;
      height: 267px !important;
      object-fit: cover;
    }
  }
`

const DetailsImageSwiperCard: React.FC<DetailsImageSwiperCardProps> = ({
  item,
}) => {
  const {
    images,
    image,
    title,
    text,
    link,
    contactTitle,
    mail,
    phone,
    facebookLink,
  } = transformProperties(item?.properties)
  const currImages = images?.mediaItems || image?.mediaItems

  return (
    <StyledDetailsImageSwiperCard>
      <StyledSwiper loop={true} pagination={true} modules={[Pagination]}>
        {currImages &&
          currImages?.map((image: { url: string | null | undefined }) => {
            return (
              <StyledSwiperSlide key={image?.url}>
                {image?.url && <Image src={image.url} alt={"image"} fill />}
              </StyledSwiperSlide>
            )
          })}
      </StyledSwiper>
      <CardDetails className="card-details">
        <StyledGeneralTextContent data={{ title, text, link }} />
        {contactTitle && <Line />}
        <StyledLinksContainer>
          {contactTitle && (
            <Typography variant="h5">{contactTitle?.value}</Typography>
          )}
          {mail &&
            mail.links?.map((link: any) => {
              return (
                <CircleIconLink
                  iconName="mail"
                  target={link?.target || "_blank"}
                  href={`mailto:${link?.url}`}
                  key={link?.url}
                >
                  {link?.name}
                </CircleIconLink>
              )
            })}
          {phone &&
            phone.links?.map((link: any) => {
              return (
                <CircleIconLink
                  iconName="phone"
                  target={link?.target || "_blank"}
                  href={`tel:${link?.url}`}
                  key={link?.url}
                >
                  {link?.name}
                </CircleIconLink>
              )
            })}
          {facebookLink &&
            facebookLink.links?.map((link: any) => {
              return (
                <CircleIconLink
                  iconName="facebookLink"
                  target={link?.target || "_blank"}
                  href={link?.url}
                  key={link?.url}
                />
              )
            })}
        </StyledLinksContainer>
      </CardDetails>
    </StyledDetailsImageSwiperCard>
  )
}

export default DetailsImageSwiperCard
